<template>
  <div>
    <v-card
      outlined
      tile
      class="my-5"
      v-show="showEndUserAssessment"
    >
      <v-card-title>
        The following activities require completion of the MSA end-user assessment.
      </v-card-title>

      <v-card-text>
        <ul>
          <li
            v-for="activity of endUserActivities"
            :key="activity._id"
          >
            <span class="font-weight-medium">{{ _.get(activity, 'memberType.name', activity.type) }}</span>
            <template v-if="activity.subType">
              - {{ _.get(subTypeMap, [activity.type, activity.subType, 'name'], activity.subType) }}
            </template>
          </li>
        </ul>
      </v-card-text>

      <v-card-actions>
        <v-btn
          name="end-user-assessment"
          color="primary"
          :disabled="disableInput"
          @click="$emit('endUserAssessment')"
        >
          <v-icon>assessment</v-icon>
          Take Assessment
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      outlined
      tile
      class="my-5"
      v-show="showProducerAssessment"
    >
      <v-card-title>
        The following activities require completion of the MSA producer assessment.
      </v-card-title>

      <v-card-text>
        <ul>
          <li
            v-for="activity of producerActivities"
            :key="activity._id"
          >
            <span class="font-weight-medium">{{ _.get(activity, 'memberType.name', activity.type) }}</span>
            <template v-if="activity.subType">
              - {{ _.get(subTypeMap, [activity.type, activity.subType, 'name'], activity.subType) }}
            </template>
          </li>
        </ul>
      </v-card-text>

      <v-card-actions>
        <v-btn
          name="producer-assessment"
          color="primary"
          :disabled="disableInput"
          @click="$emit('producerAssessment')"
        >
          <v-icon>assessment</v-icon>
          Take Assessment
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      outlined
      tile
      class="my-5"
    >
      <v-card-title>
        The following activities may be submitted.
      </v-card-title>

      <v-row
        v-for="activity of activities"
        v-show="activities.length > 0"
        :key="activity._id"
        class="mx-1"
      >
        <v-col class="grow">
          <p class="font-weight-medium">
            <span class="font-weight-medium">{{ _.get(activity, 'memberType.name', activity.type) }}</span>
            <template v-if="activity.subType">
              - {{ _.get(subTypeMap, [activity.type, activity.subType, 'name'], activity.subType) }}
            </template>
          </p>
          <p v-if="activity.memberType.prerequisitesDescription">
            {{ activity.memberType.prerequisitesDescription }}
          </p>
        </v-col>
        <v-col class="shrink text-end">
          <v-btn
            color="primary"
            :disabled="activity.registrationStatus !== RegistrationStatus.INITIAL"
            @click="beginSubmit(activity)"
            small
          >
            <template v-if="activity.registrationStatus === RegistrationStatus.INITIAL">Submit</template>
            <template v-else-if="activity.registrationStatus === RegistrationStatus.SUBMITTED">Submitted</template>
            <template v-else>Completed</template>
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-dialog
      v-model="submitDialog"
      width="600"
    >
      <v-card v-if="selectedActivity">
        <v-card-title class="text-h5 grey lighten-2">
          {{ _.get(selectedActivity, 'memberType.name', selectedActivity.type) }}
          <template v-if="selectedActivity.subType">
            - {{ _.get(subTypeMap, [selectedActivity.type, selectedActivity.subType, 'name'], selectedActivity.subType) }}
          </template>
        </v-card-title>

        <v-row class="ma-1">
          <v-col>
            <registration-agreement
              v-if="submitDialog"
              :registrationId="registrationId"
              :activityId="selectedActivity._id"
              @save="onLicenseAccepted"
              v-bubble.error
            />
          </v-col>
        </v-row>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn
            :disabled="!licenseAccepted || submitting"
            :loading="submitting"
            color="primary"
            text
            @click="submitActivity"
          >
            Submit application
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { RegistrationStatus } from '@mymsa/common'

import RegistrationAgreement from './RegistrationAgreement.vue'

export default {
  name: 'RegistrationActivitiesSubmit',

  props: {
    registrationId: {
      type: String,
      required: true
    },
    disableInput: {
      type: Boolean,
      default: false
    }
  },

  components: {
    RegistrationAgreement
  },

  data: () => ({
    RegistrationStatus,
    registration: {
      activities: []
    },
    memberTypes: [],
    submitDialog: false,
    licenseAccepted: false,
    selectedActivity: null,
    submitting: false
  }),

  computed: {
    endUserActivities () {
      return this.registration.activities
        .filter(o => o.memberType.enableEndUserPortal)
    },

    producerActivities () {
      return this.registration.activities
        .filter(o => o.memberType.enableProducerPortal)
    },

    activities () {
      const {
        endUserAssessmentComplete,
        producerAssessmentComplete
      } = this.registration
      return this.registration.activities
        .filter(o => endUserAssessmentComplete || !o.memberType.enableEndUserPortal)
        .filter(o => producerAssessmentComplete || !o.memberType.enableProducerPortal)
    },

    subTypeMap () {
      return Object.fromEntries(this.memberTypes.map(memberType => [
        memberType.value,
        Object.fromEntries((memberType.subTypes ?? []).map(subType => [
          subType.value,
          subType
        ]))
      ]))
    },

    showEndUserAssessment () {
      return this.endUserActivities.length > 0 &&
        !this.registration.endUserAssessmentComplete
    },

    showProducerAssessment () {
      return this.producerActivities.length > 0 &&
        !this.registration.producerAssessmentComplete
    }
  },

  apollo: {
    registration: {
      query: gql`query GetRegistrationActivitiesForSubmit($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          endUserAssessmentComplete
          producerAssessmentComplete
          activities {
            _id
            registrationStatus
            type
            subType
            memberType {
              _id
              name
              prerequisitesDescription
              trainingRequired
              siteVisitRequired
              enableEndUserPortal
              enableProducerPortal
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      error (e) { this.onError(e) }
    },

    memberTypes: {
      query: gql`query {
        memberTypes: memberTypeMany(limit: 0) {
          _id
          value
          subTypes {
            _id
            value
            name
          }
        }
      }`,
      error (e) { this.onError(e) }
    }
  },

  methods: {
    onError (e) {
      this.$emit('error', e)
    },

    beginSubmit (activity) {
      this.selectedActivity = activity
      this.licenseAccepted = false
      this.submitDialog = true
    },

    onLicenseAccepted (e) {
      this.licenseAccepted = true
    },

    async submitActivity () {
      this.submitting = true
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $activityId: MongoID!) {
            registrationSubmitActivity(_id: $id, activityId: $activityId)
          }`,
          variables: {
            id: this.registrationId,
            activityId: this.selectedActivity._id
          }
        })
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.submitting = false
      }
      this.selectedActivity = null
      this.submitDialog = false
      this.$apollo.queries.registration.refresh()
    }
  }
}
</script>
